import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { MdChat } from 'react-icons/md';
import clsx from 'clsx';
import useFetchIam from '../api/useFetchIam';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import Breadcrumb from '@/components/Breadcrumb';
import { useSetAtom, useAtom } from 'jotai';
import { trialConsultationModalAtom } from '../components/TrialConsultationPopup';
import HeaderMessagePopup, { headerMessagePopupAtom } from '../components/HeaderMessagePopup';
import useFetchCandidateChatThreads from '../candidates/api/useFetchCandidateChatThreads';

export default function Header() {
  const { data: iam } = useFetchIam();
  const { breadcrumbItems } = useBreadcrumb();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const openTrialModal = useSetAtom(trialConsultationModalAtom);
  const [isMessageOpen, setIsMessageOpen] = useAtom(headerMessagePopupAtom);
  // 未読ドットは担当有無に関わらず組織全体の求職者で判定する
  const { data: threadsData } = useFetchCandidateChatThreads('all');
  const hasUnread = threadsData?.pages[0]?.has_unread ?? false;
  const currentUser = iam?.current_user;

  return (
    <div className="l-header" id="agent-header">
      <div className="l-breadcrumb">
        {breadcrumbItems.length > 0 && <Breadcrumb items={breadcrumbItems} />}
      </div>
      <ul className="l-headerNav">
        {iam?.plan_type === 'trial' &&
          iam?.trial_days_remaining !== null &&
          iam.trial_days_remaining <= 5 && (
            <li className="flex items-center">
              <div className="flex h-[28px] items-center justify-center rounded bg-danger_lighten px-sm">
                <span className="text-heading-xxs text-danger">
                  終了まであと{iam.trial_days_remaining}日
                </span>
              </div>
            </li>
          )}
        {iam?.plan_type === 'trial' && (
          <li
            className="l-headerNavItem"
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '8px',
              gap: '4px',
              cursor: 'pointer',
            }}
            onClick={() => openTrialModal(true)}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #005fff 0%, #ed72f8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              トライアル版
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="headerTrialGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#005fff" />
                  <stop offset="100%" stopColor="#ed72f8" />
                </linearGradient>
              </defs>
              <path
                d="M6 22C5.45 22 4.97917 21.8042 4.5875 21.4125C4.19583 21.0208 4 20.55 4 20V10C4 9.45 4.19583 8.97917 4.5875 8.5875C4.97917 8.19583 5.45 8 6 8H7V6C7 4.61667 7.4875 3.4375 8.4625 2.4625C9.4375 1.4875 10.6167 1 12 1C13.3833 1 14.5625 1.4875 15.5375 2.4625C16.5125 3.4375 17 4.61667 17 6V8H18C18.55 8 19.0208 8.19583 19.4125 8.5875C19.8042 8.97917 20 9.45 20 10V20C20 20.55 19.8042 21.0208 19.4125 21.4125C19.0208 21.8042 18.55 22 18 22H6ZM12 17C12.55 17 13.0208 16.8042 13.4125 16.4125C13.8042 16.0208 14 15.55 14 15C14 14.45 13.8042 13.9792 13.4125 13.5875C13.0208 13.1958 12.55 13 12 13C11.45 13 10.9792 13.1958 10.5875 13.5875C10.1958 13.9792 10 14.45 10 15C10 15.55 10.1958 16.0208 10.5875 16.4125C10.9792 16.8042 11.45 17 12 17ZM9 8H15V6C15 5.16667 14.7083 4.45833 14.125 3.875C13.5417 3.29167 12.8333 3 12 3C11.1667 3 10.4583 3.29167 9.875 3.875C9.29167 4.45833 9 5.16667 9 6V8Z"
                fill="url(#headerTrialGrad)"
              />
            </svg>
          </li>
        )}
        <li className="flex items-center">
          <button
            type="button"
            onClick={() => setIsMessageOpen(!isMessageOpen)}
            className={clsx(
              'relative flex size-10.5 items-center justify-center rounded',
              isMessageOpen ? 'bg-gray_darken' : 'hover:bg-gray'
            )}
            aria-label="メッセージ"
            aria-expanded={isMessageOpen}
          >
            <MdChat size={24} className="text-main" />
            {hasUnread && (
              <span className="absolute top-2 right-2 size-2 rounded-full bg-orange-600" />
            )}
          </button>
        </li>
        <li
          className={`l-headerNavItem l-headerNavItem--dropdown nav-item dropdown user-menu ${isDropdownOpen ? 'show' : ''}`}
        >
          <button
            className="l-headerDropdown__toggle dropdown-toggle"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            type="button"
            aria-expanded={isDropdownOpen}
          >
            <span data-user-name={currentUser?.name}>{currentUser?.name || ''}</span>
          </button>
          {isDropdownOpen && (
            <>
              <div
                className="dropdown-backdrop l-headerDropdown__backdrop"
                onClick={() => setIsDropdownOpen(false)}
              />
              <div className="l-headerDropdown__menu dropdown-menu dropdown-menu-lg dropdown-menu-right">
                <Link
                  to="/agent/settings"
                  className="l-headerDropdown__logout dropdown-item text-sm"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  設定
                </Link>
                <a
                  href="/agent/tos"
                  className="l-headerDropdown__logout dropdown-item text-sm"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  利用規約
                </a>
                <form action="/agent/sign_out" method="post">
                  <input type="hidden" name="_method" value="delete" />
                  <input
                    type="hidden"
                    name="authenticity_token"
                    value={
                      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ||
                      ''
                    }
                  />
                  <button type="submit" className="l-headerDropdown__logout dropdown-item text-sm">
                    ログアウト
                  </button>
                </form>
              </div>
            </>
          )}
        </li>
      </ul>
      <HeaderMessagePopup />
    </div>
  );
}
